<template>
  <div class="activity-page-edit">
    <!-- 頂部操作欄 -->
    <header class="edit-header">
      <div class="header-left">
        <el-button size="small" icon="el-icon-back" @click="goBack">返回</el-button>
        <h2 class="page-title">{{ isEdit ? '編輯活動頁' : '新增活動頁' }}</h2>
      </div>
      <div class="header-right">
        <el-button size="small" type="primary" :loading="saving" icon="el-icon-check" @click="handleSave">
          保存
        </el-button>
      </div>
    </header>

    <div class="edit-body">
      <!-- Banner 與基礎設定 -->
      <section class="panel">
        <div class="panel-title">Banner 設定</div>
        <el-form ref="baseForm" :model="form" :rules="rules" label-width="160px" size="small">
          <el-form-item label="品牌">
            <el-select v-model="form.brand" placeholder="請選擇品牌" style="width: 220px">
              <el-option label="HONGQI" value="hongqi"></el-option>
              <el-option label="ZEEKR" value="zeekr"></el-option>
              <el-option label="ICAUR" value="icaur"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="Banner(Desktop)" prop="bannerDesktop">
            <image-uploader v-model="form.bannerDesktop" tip="建議尺寸 1920×800，jpg/png，≤5MB" />
          </el-form-item>
          <el-form-item label="Banner(Mobile)" prop="bannerMobile">
            <image-uploader v-model="form.bannerMobile" tip="建議尺寸 750×1000，jpg/png，≤5MB" />
          </el-form-item>
          <el-form-item label="Banner Title(En)">
            <el-input
              v-model="form.bannerTitleEn"
              placeholder="Please enter banner title (EN)"
              maxlength="100"
            ></el-input>
          </el-form-item>
          <el-form-item label="Banner Title(Tc)">
            <el-input v-model="form.bannerTitleTc" placeholder="請輸入 Banner 標題（繁中）" maxlength="100"></el-input>
          </el-form-item>
          <el-form-item label="Banner Title Color">
            <el-color-picker v-model="form.bannerTitleColor"></el-color-picker>
          </el-form-item>
          <el-form-item label="Banner Content(En)">
            <el-input
              v-model="form.bannerContentEn"
              type="textarea"
              :rows="3"
              placeholder="Please enter banner content (EN)"
              maxlength="500"
            ></el-input>
          </el-form-item>
          <el-form-item label="Banner Content(Tc)">
            <el-input
              v-model="form.bannerContentTc"
              type="textarea"
              :rows="3"
              placeholder="請輸入 Banner 內容（繁中）"
              maxlength="500"
            ></el-input>
          </el-form-item>
          <el-form-item label="Banner Content Color">
            <el-color-picker v-model="form.bannerContentColor"></el-color-picker>
          </el-form-item>
          <el-form-item label="報名時間">
            <el-date-picker
              v-model="form.registrationTime"
              type="datetimerange"
              range-separator="至"
              start-placeholder="開始時間"
              end-placeholder="結束時間"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
          <el-form-item label="總人數限制">
            <el-input-number
              v-model="form.totalLimit"
              :min="0"
              :max="999999"
              controls-position="right"
            ></el-input-number>
            <span class="field-hint">0 表示不限制</span>
          </el-form-item>
          <el-form-item label="每天限制報名數">
            <el-input-number
              v-model="form.dailyLimit"
              :min="0"
              :max="99999"
              controls-position="right"
            ></el-input-number>
            <span class="field-hint">0 表示不限制</span>
          </el-form-item>
        </el-form>
      </section>

      <!-- 網格排版 -->
      <section class="panel">
        <div class="panel-title">
          網格排版
          <span class="panel-sub">（客戶端展示順序，可拖拽或上下移動排序）</span>
        </div>
        <div class="module-actions">
          <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addModule('text')">
            添加文本
          </el-button>
          <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addModule('form')">
            添加表單
          </el-button>
          <el-button size="small" type="primary" plain icon="el-icon-plus" @click="addModule('video')">
            添加視頻
          </el-button>
        </div>

        <div v-if="!form.modules.length" class="empty-modules">暫無模塊，請点击上方按鈕添加</div>

        <draggable v-model="form.modules" handle=".drag-handle" animation="200" class="module-list">
          <div v-for="(mod, idx) in form.modules" :key="mod._key" class="module-card">
            <div class="module-head">
              <i class="el-icon-rank drag-handle" title="拖拽排序"></i>
              <el-tag size="mini" :type="typeTag(mod.type)">{{ typeLabel(mod.type) }}</el-tag>
              <span class="module-index">第 {{ idx + 1 }} 個</span>
              <span class="module-name">{{ mod.title || typeLabel(mod.type) }}</span>
              <div class="module-ops">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-top"
                  :disabled="idx === 0"
                  @click="moveModule(idx, -1)"
                ></el-button>
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-bottom"
                  :disabled="idx === form.modules.length - 1"
                  @click="moveModule(idx, 1)"
                ></el-button>
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-delete"
                  class="danger-btn"
                  @click="removeModule(idx)"
                ></el-button>
              </div>
            </div>

            <div class="module-body">
              <!-- 文本模塊 -->
              <template v-if="mod.type === 'text'">
                <el-form label-width="90px" size="small" class="inner-form">
                  <el-form-item label="模塊標題">
                    <el-input v-model="mod.title" placeholder="可選，模塊標題" maxlength="60"></el-input>
                  </el-form-item>
                  <el-form-item label="對齊方式">
                    <el-radio-group v-model="mod.align">
                      <el-radio-button label="left">左對齊</el-radio-button>
                      <el-radio-button label="center">居中</el-radio-button>
                      <el-radio-button label="right">右對齊</el-radio-button>
                    </el-radio-group>
                  </el-form-item>
                  <el-form-item label="文字顏色">
                    <el-color-picker v-model="mod.color"></el-color-picker>
                  </el-form-item>
                  <el-form-item label="文本內容">
                    <rich-text-editor v-model="mod.content" placeholder="請輸入文本內容" />
                  </el-form-item>
                </el-form>
              </template>

              <!-- 表單模塊（佔位，待設計） -->
              <template v-else-if="mod.type === 'form'">
                <el-form label-width="90px" size="small" class="inner-form">
                  <el-form-item label="模塊標題">
                    <el-input v-model="mod.title" placeholder="可選，模塊標題" maxlength="60"></el-input>
                  </el-form-item>
                </el-form>
                <div class="placeholder-box">
                  <i class="el-icon-s-order"></i>
                  表單模塊（功能待設計，後續安排）
                </div>
              </template>

              <!-- 視頻模塊（佔位） -->
              <template v-else-if="mod.type === 'video'">
                <el-form label-width="90px" size="small" class="inner-form">
                  <el-form-item label="模塊標題">
                    <el-input v-model="mod.title" placeholder="可選，模塊標題" maxlength="60"></el-input>
                  </el-form-item>
                  <el-form-item label="視頻地址">
                    <el-input v-model="mod.videoUrl" placeholder="請輸入視頻 URL（如 mp4 / YouTube 連結）"></el-input>
                  </el-form-item>
                </el-form>
                <div class="placeholder-box">
                  <i class="el-icon-video-camera"></i>
                  視頻模塊（展示樣式待設計）
                </div>
              </template>
            </div>
          </div>
        </draggable>
      </section>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import ImageUploader from '@/components/admin/pageBuilder/ImageUploader.vue'
import RichTextEditor from '@/components/admin/pageBuilder/RichTextEditor.vue'
import { activityPageDetailApi, activityPageSaveApi } from '@/Api/requestApi'

let keySeed = 0
function genKey() {
  keySeed += 1
  return `m_${Date.now()}_${keySeed}`
}

export default {
  name: 'ActivityPageEdit',
  components: { draggable, ImageUploader, RichTextEditor },
  data() {
    return {
      saving: false,
      isEdit: false,
      form: {
        id: null,
        brand: 'zeekr',
        bannerDesktop: '',
        bannerMobile: '',
        bannerTitleEn: '',
        bannerTitleTc: '',
        bannerTitleColor: '#ffffff',
        bannerContentEn: '',
        bannerContentTc: '',
        bannerContentColor: '#ffffff',
        registrationTime: [],
        totalLimit: 0,
        dailyLimit: 0,
        modules: [],
      },
      rules: {
        bannerDesktop: [{ required: true, message: '請上傳 Banner(Desktop)', trigger: 'change' }],
        bannerMobile: [{ required: true, message: '請上傳 Banner(Mobile)', trigger: 'change' }],
      },
    }
  },
  created() {
    const { id, brand } = this.$route.query
    if (brand) this.form.brand = brand
    if (id) {
      this.isEdit = true
      this.form.id = id
      this.loadDetail(id)
    }
  },
  methods: {
    async loadDetail(id) {
      try {
        const { data } = await activityPageDetailApi({ id })
        if (data.code === 200 && data.data) {
          const d = data.data
          this.form = {
            ...this.form,
            ...d,
            registrationTime: d.registrationTime || [],
            modules: (d.modules || []).map((m) => ({
              _key: genKey(),
              type: m.type || 'text',
              title: m.title || '',
              align: m.align || 'left',
              color: m.color || '#333333',
              content: m.content || '',
              videoUrl: m.videoUrl || '',
            })),
          }
        }
      } catch (err) {
        console.error('獲取活動頁面詳情失敗:', err)
      }
    },
    addModule(type) {
      const base = { _key: genKey(), type, title: '' }
      if (type === 'text') {
        this.form.modules.push({ ...base, align: 'left', color: '#333333', content: '' })
      } else if (type === 'form') {
        this.form.modules.push({ ...base, title: '報名表單' })
      } else if (type === 'video') {
        this.form.modules.push({ ...base, videoUrl: '' })
      }
    },
    removeModule(idx) {
      this.$confirm('確定要刪除該模塊嗎？', '提示', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.form.modules.splice(idx, 1)
        })
        .catch(() => {})
    },
    moveModule(idx, dir) {
      const target = idx + dir
      if (target < 0 || target >= this.form.modules.length) return
      const list = this.form.modules
      const tmp = list[idx]
      this.$set(list, idx, list[target])
      this.$set(list, target, tmp)
    },
    typeLabel(type) {
      return { text: '文本', form: '表單', video: '視頻' }[type] || type
    },
    typeTag(type) {
      return { text: '', form: 'success', video: 'warning' }[type] || 'info'
    },
    buildPayload() {
      return {
        ...this.form,
        modules: this.form.modules.map((m) => {
          const mo = { ...m }
          delete mo._key
          return mo
        }),
      }
    },
    handleSave() {
      this.$refs.baseForm.validate(async (valid) => {
        if (!valid) {
          this.$message({ message: '請完善 Banner 必填項', type: 'warning' })
          return
        }
        this.saving = true
        try {
          const payload = this.buildPayload()
          const { data } = await activityPageSaveApi(payload)
          if (data.code === 200) {
            this.$message({ message: '保存成功', type: 'success' })
            this.goBack()
          } else {
            this.$message({ message: data.message || '保存失敗', type: 'error' })
          }
        } catch (err) {
          console.error('保存活動頁面失敗:', err)
        } finally {
          this.saving = false
        }
      })
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace({ path: '/RTS/KLM/dashboard' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.activity-page-edit {
  min-height: 100vh;
  background: #f0f2f5;
}

.edit-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 16px;
  background: #1a1a2e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .page-title {
      margin: 0;
      font-size: 17px;
      color: #fff;
      font-weight: 700;
    }
  }
}

.edit-body {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px 12px 60px;
}

.panel {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px 4px;
  margin-bottom: 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  .panel-title {
    font-size: 16px;
    font-weight: 700;
    color: #303133;
    padding-bottom: 12px;
    margin-bottom: 16px;
    border-bottom: 1px solid #ebeef5;

    .panel-sub {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }
}

.field-hint {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.module-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.empty-modules {
  padding: 30px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 13px;
}

.module-list {
  .module-card {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    margin-bottom: 12px;
    background: #fff;
    overflow: hidden;

    .module-head {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 12px;
      background: #f5f7fa;
      border-bottom: 1px solid #ebeef5;

      .drag-handle {
        cursor: move;
        color: #909399;
        font-size: 16px;
      }

      .module-index {
        font-size: 12px;
        color: #909399;
      }

      .module-name {
        font-size: 13px;
        color: #303133;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
      }

      .module-ops {
        display: flex;
        align-items: center;
        gap: 2px;
        margin-left: auto;

        .danger-btn {
          color: #f56c6c;
        }
      }
    }

    .module-body {
      padding: 14px 12px 2px;

      .inner-form {
        ::v-deep .el-form-item {
          margin-bottom: 14px;
        }
      }
    }
  }
}

.placeholder-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 26px;
  margin-bottom: 12px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  color: #909399;
  font-size: 13px;
  background: #fafafa;

  i {
    font-size: 18px;
  }
}

.danger-btn {
  color: #f56c6c;
}

/* 手機端適配 */
@media (max-width: 768px) {
  .edit-body {
    padding: 12px 8px 40px;
  }

  .panel {
    padding: 12px 12px 2px;
  }

  ::v-deep .el-form-item__label {
    float: none;
    display: block;
    width: 100% !important;
    text-align: left;
    padding: 0 0 6px;
    line-height: 1.4;
  }

  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
  }

  .module-actions {
    ::v-deep .el-button {
      flex: 1;
    }
  }
}
</style>
